import {
  takeLatest,
  call,
  put,
  select,
  all,
  cancel,
  cancelled,
} from 'redux-saga/effects';
import _ from 'lodash';
import dayjs from 'dayjs';
import { parsePhoneNumber } from 'react-phone-number-input';
import analytics from '../../../utils/analytics';
import axios from '../axiosConfig';
import {
  bookTripRequest,
  bookTripSuccess,
  bookTripFailure,
  getTripConfirmationSuccess,
  changeValidation,
  changeTrip,
  getTripSuccess,
  setCruiseItineraryPaymentAmount,
  selectedCreditCard,
  doUseSavedCardForCheckout,
  pollBookingRequest,
  getCustomerIdToSaveCard,
} from '../../slices/trip';
import {
  getSavedCardsRequest,
} from '../../slices/customer';
import {
  BookingAction,
  CreditCardCodes,
  SearchStatus,
  SecurityInformationTypes,
  TravelTypes,
  TripConfirmationChanges,
  AgencyPlanningType,
} from '../../../constants';
import validateCheckoutForms from '../../../helpers/validationHelpers/validateCheckoutForms';
import { handleCardTokenization } from '../../../helpers/tokenizeCards';
import { formatBookingError } from '../../../helpers/formatBookingError';
import { buildCreditCardPayments } from '../../../helpers/buildCreditCardPayment';
import getVGSProxyEndpoint from '../../../helpers/getVGSProxyEndpoint';
import getPriceBreakdownKey from '../../../helpers/getPriceBreakdownKey';
import formatPriceRequest from '../../../helpers/formatPriceRequest';
import getCabinPrice from '../../../helpers/getCabinPrice';
import getTripModes from '../../../helpers/getTripModes';
import validateEmail from '../../../helpers/validationHelpers/validateEmail';
import checkDoShowSavePassenger from '../../../helpers/checkDoShowSavePassenger';
import getEmergencyContactByPassenger from '../../../helpers/getEmergencyContactByPassenger';
import validateCustomerIdentifier from '../../../helpers/validationHelpers/validateCustomerIdentifier';
import validateRequestorIATA from '../../../helpers/validationHelpers/validateRequestorIATA';
import validatePromoCode from '../../../helpers/validationHelpers/validatePromoCode';
import checkDoChargeAgencyPlanningFees from '../../../helpers/selectors/checkDoChargeAgencyPlanningFees';
import getAcceptedPaymentCards from '../../../helpers/getAcceptedPaymentCards';
import formatGuestInfoForVerifyingTripConfirmationAccess from '../../../helpers/formatters/formatGuestInfoForVerifyingTripConfirmationAccess';

function* bookTripWorker({
  payload: {
    tripId,
    doReturnAvailabilityError,
    doReturnPriceChangeError,
    tripConfirmationChanges,
    doAcceptPriceQuoteChange,
    trackingParams,
    tokenizeFunc,
    isFormValid,
  },
}) {
  let tokenizedCardMap = null;
  const tokenizedDataForValidation = tokenizeFunc && isFormValid ? { isTokenized: true } : null;
  let primaryPassengerInfo;
  try {
    yield call([analytics, 'track'], 'bookTripWorker.bookTrip.start', { tripId });
    let { doChargeAgencyPlanningFees } = yield select((store) => store.trip);
    const timeZoneOffset = new Date().getTimezoneOffset();
    const {
      passengers, creditCards, specialBookingRequests,
      tripConfirmation, emptyPassengerDetailValidation,
      agencyPlanningFees, selectedAdditionalCheckoutFields,
      hasReviewedBookedTripPolicy, hasAgreedForDataCollection,
      hasReviewedBookingTripPolicy,
      isBookingReviewManual, agencyPlanningType,
      agencyPlanningFeesKeptByAgency, doSplitServiceFees,
      customerIdentifier, requestorIATA, travelAgencyAdvisorsEmail, promoCode,
      noteAirDeskRequest, userRemarks, chargeableItemOptions,
    } = yield select((store) => store.trip);

    const doChageFees = yield select(checkDoChargeAgencyPlanningFees(tripId));
    if (!doChageFees) {
      doChargeAgencyPlanningFees = false;
    }

    const { priceBreakdowns } = yield select((store) => store.cruise);

    const trip = yield select((store) => store.trip.selectedTrips[tripId]);

    const {
      tripCode, cruiseItinerary, cruisePayments, doChangeItineraryInBookedTrip,
      itinerariesOnHold, addPaymentMethodToTravelTypes,
      hotelItineraries, isTripQuote, brochureId,
    } = trip;

    let url = '/trips/book';
    if (tripConfirmationChanges && tripConfirmationChanges.hasChanges) {
      url = '/trips/updateBooking';
    } else if (isTripQuote) {
      url = '/trips/book/tripquote';
    } else if (brochureId) {
      url = '/trips/book/brochure';
    }

    const {
      availableStates, isLoggedIn, travelAgency, email,
    } = yield select((store) => store.user);

    const loggedInUserEmail = email && email.length ? email : '';
    const selectedCardForCheckout = yield select(selectedCreditCard);
    const doUseSavedCardForBooking = yield select(doUseSavedCardForCheckout);

    const targetCards = Object.values(creditCards).filter((card) => card.isPicked
      && card.expiration && card.cardNumber && CreditCardCodes[card.component]);
    const {
      itinerariesToBookWithPaymentMethod, itinerariesToBook,
    } = getTripModes(trip, travelAgency);

    let isBooking = tripConfirmationChanges ? tripConfirmationChanges.doAddPaymentMethod
      : itinerariesToBookWithPaymentMethod.length > 0
      || (doChargeAgencyPlanningFees && !trip.itinerariesOnHold?.length);

    if (isBooking && !isLoggedIn && (travelAgency || {}).useCardFromAgencyProfile) {
      isBooking = false;
    }
    const doHoldOnlyCruiseAsOption = !tripConfirmation && (itinerariesOnHold || []).length === 1
      && (itinerariesOnHold[0] || {}).itineraryMode === TravelTypes.cruise;

    // cleaned creditCards object with only valid selectedFor items
    const values = chargeableItemOptions.map((o) => o.value);
    const updatedCreditCards = Object.fromEntries(
      Object.entries(creditCards).map(([key, card]) => [
        key,
        {
          ...card,
          selectedFor: card.selectedFor && card.selectedFor.length
            ? card.selectedFor.filter((v) => values.includes(v)) : undefined,
        },
      ]),
    );

    const hasHotels = hotelItineraries && Object.keys(hotelItineraries).length > 0;
    const acceptedCards = hasHotels ? getAcceptedPaymentCards(Array.isArray(hotelItineraries)
      ? hotelItineraries : Object.values(hotelItineraries)) : {};
    const [
      isValidForms,
      newPassengersFormsValidation,
      creditCardsFormValidations,
      similarPassengersValidation,
      _emptyPassengerDetailValidation,
      isBookedTripPolicyReviewValidated,
      isBookingTripPolicyReviewValidated,
      isSavedCardValidated,
      isAgencyPlanningFeesValid,
      invalidAdditionalCheckoutFields,
      isNoteAirDeskValidated,
      isAgreedForDataCollection,
      isUserRemarksValidated,
    ] = validateCheckoutForms(trip, passengers, updatedCreditCards, availableStates, isBooking,
      travelAgency, tripConfirmation, !!(emptyPassengerDetailValidation || {}).doIgnore,
      doChargeAgencyPlanningFees, agencyPlanningFees, hasReviewedBookingTripPolicy,
      doChangeItineraryInBookedTrip, hasReviewedBookedTripPolicy, isLoggedIn,
      doHoldOnlyCruiseAsOption, doUseSavedCardForBooking, selectedCardForCheckout,
      itinerariesToBook, doSplitServiceFees, agencyPlanningFeesKeptByAgency, acceptedCards,
      isBookingReviewManual, noteAirDeskRequest,
      hasAgreedForDataCollection, selectedAdditionalCheckoutFields,
      userRemarks, chargeableItemOptions, tokenizedDataForValidation, loggedInUserEmail);

    if (!isValidForms) {
      if (!_.isEmpty(_emptyPassengerDetailValidation || {})) {
        yield put(changeValidation({
          emptyPassengerDetailValidation: _emptyPassengerDetailValidation,
        }));
      } else {
        const effects = newPassengersFormsValidation.map((formValidations, targetIndex) => (
          put(changeValidation({
            field: 'passengers',
            targetIndex,
            formValidations,
            similarPassengersValidation,
            isSavedCardValidated,
            isAgencyPlanningFeesValid,
            invalidAdditionalCheckoutFields,
          }))
        ));
        yield all(effects);
        yield put(changeValidation({
          creditCardsValidations: creditCardsFormValidations,
          similarPassengersValidation,
          isBookedTripPolicyReviewValidated,
          isBookingTripPolicyReviewValidated,
          isNoteAirDeskValidated,
          isSavedCardValidated,
          isAgencyPlanningFeesValid,
          invalidAdditionalCheckoutFields,
          isAgreedForDataCollection,
          isUserRemarksValidated,
        }));
      }
      yield cancel();
    }

    if ((tripConfirmationChanges && tripConfirmationChanges.doAddPaymentMethod
      && addPaymentMethodToTravelTypes.length === 0)
      || validateCustomerIdentifier(customerIdentifier) || validateRequestorIATA(requestorIATA)) {
      yield cancel();
    }
    if (validatePromoCode(promoCode)) {
      yield cancel();
    }

    // Handle tokenization
    try {
      tokenizedCardMap = yield call(
        handleCardTokenization,
        tokenizeFunc,
        isFormValid,
        doUseSavedCardForBooking,
      );
    } catch (error) {
      yield call([analytics, 'track'], 'bookTripWorker.bookTrip.tokenizeCard.fail',
        { tripId, error: JSON.stringify(error.message) });
      yield put(bookTripFailure({ error: error.message }));
      yield cancel();
    }

    const doIncludeCC = isBooking
      && (targetCards.length > 0 || (tokenizedCardMap && Object.keys(tokenizedCardMap).length > 0));
    let cruiseTotalAmounts = null;
    if (cruiseItinerary) {
      // To recheck price before book
      const pricingKey = getPriceBreakdownKey(
        formatPriceRequest({
          itinerary: trip.cruiseItinerary,
          passengers,
          bookingResponses: tripConfirmation && tripConfirmation.bookingResponses,
        }),
      );
      if (priceBreakdowns[pricingKey]) {
        cruiseTotalAmounts = _.map(priceBreakdowns[pricingKey],
          (pricing) => getCabinPrice(pricing));
      }
    }

    const tripChanges = {};
    if (tripConfirmationChanges && tripConfirmationChanges.doAddPaymentMethod
      && addPaymentMethodToTravelTypes && addPaymentMethodToTravelTypes.length) {
      _.each(addPaymentMethodToTravelTypes, (mode) => {
        tripChanges[mode] = [TripConfirmationChanges.Payment];
      });
    }
    if (tripConfirmationChanges && tripConfirmationChanges.hasChanges) {
      tripChanges[TravelTypes.cruise] = tripChanges[TravelTypes.cruise] || [];
      if ((tripConfirmationChanges.packageChanges || []).length > 0) {
        tripChanges[TravelTypes.cruise].push(TripConfirmationChanges.Packages);
      }
      if ((tripConfirmationChanges.servicesChanges || []).length > 0) {
        tripChanges[TravelTypes.cruise].push(TripConfirmationChanges.SpecialServices);
      }
      if ((tripConfirmationChanges.insuranceChanges || []).length > 0) {
        tripChanges[TravelTypes.cruise].push(TripConfirmationChanges.Insurance);
      }
      if ((tripConfirmationChanges.cruiseTransportChanges || []).length > 0) {
        tripChanges[TravelTypes.cruise].push(TripConfirmationChanges.CruiseTransport);
      }
      if ((tripConfirmationChanges.diningChanges || []).length > 0) {
        tripChanges[TravelTypes.cruise].push(TripConfirmationChanges.Dining);
      }
      if (!tripChanges[TravelTypes.cruise].length) tripChanges[TravelTypes.cruise] = undefined;
    }
    const passengersByRoom = _.groupBy(Object.values(passengers), (p) => p.roomIndex);

    const sessionKey = cruiseItinerary && cruiseItinerary.cabins
      && cruiseItinerary.cabins[0] && cruiseItinerary.cabins[0].selectedShipCabinCategory
      ? cruiseItinerary.cabins[0].selectedShipCabinCategory.sessionKey
      : undefined;

    const config = {
      method: 'post',
      url: ((doIncludeCC && !(tokenizedCardMap && Object.keys(tokenizedCardMap).length > 0)) ? getVGSProxyEndpoint() : '') + url,
      data: JSON.stringify({
        tripId: tripCode,
        diningInputs: cruiseItinerary ? _.map(cruiseItinerary.cabins, (cabin, roomIndex) => ({
          roomIndex,
          selectedDiningSeat: cabin.selectedDiningSeat,
          selectedDiningTable: cabin.selectedDiningTable,
        })) : undefined,
        cruiseTotalAmounts: cruiseTotalAmounts || undefined,
        selectedDiningTable: cruiseItinerary ? cruiseItinerary.selectedDiningTable : undefined,
        loggedInUserEmail,
        specialRequests: hasHotels ? (specialBookingRequests || {}) : undefined,
        noteToAirDesk: isBookingReviewManual && noteAirDeskRequest && noteAirDeskRequest.length
          ? noteAirDeskRequest.trim() : undefined,
        doReturnAvailabilityError: doReturnAvailabilityError === true
          ? doReturnAvailabilityError : undefined,
        doReturnPriceChangeError: doReturnPriceChangeError === true
          ? doReturnPriceChangeError : undefined,
        trackingParams: trackingParams && Object.keys(trackingParams).length
          ? trackingParams : undefined,
        passengers: Object.values(passengers).map((item, index) => {
          let doSaveCustomerInfo = validateEmail(item.contactEmail);
          if (doSaveCustomerInfo) {
            doSaveCustomerInfo = checkDoShowSavePassenger(Object.values(passengers), item,
              passengersByRoom[item.roomIndex].findIndex((c) => c.passengerIndex
                === item.passengerIndex), trip.isUpdatingBookedTrip, isLoggedIn, travelAgency,
              loggedInUserEmail)
              && item.doSaveCustomerInfo;
          }
          const advancePassengerInformations = _.filter(
            item.travelerPreferences.advancePassengerInformations || [],
            (d) => d.apisType !== SecurityInformationTypes.SELECT_APIS_TYPE,
          );

          const passenger = {
            ...item,
            title: trip.cruiseItinerary ? item.title : undefined,
            phone: item.phone,
            phoneWithoutCountryCode: parsePhoneNumber(item?.phone ?? '')?.nationalNumber ?? '',
            pastPassengerNumber: item.isPastPassenger ? item.pastPassengerNumber : undefined,
            birthDate: item.birthDate ? dayjs(item.birthDate).format('YYYY-MM-DD') : undefined,
            id: index,
            doSaveCustomerInfo,
            travelerPreferences: {
              ...item.travelerPreferences,
              flightMealPreference: item.travelerPreferences.flightMealPreference
                ? item.travelerPreferences.flightMealPreference.value : undefined,
              flightSeatPreference: item.travelerPreferences.flightSeatPreference
                ? item.travelerPreferences.flightSeatPreference.value : undefined,
              flightSpecialAssistance: item.travelerPreferences.flightSpecialAssistance
                ? item.travelerPreferences.flightSpecialAssistance.value : undefined,
              frequentFlyerNumbers: item.travelerPreferences.frequentFlyerNumbers
                && item.travelerPreferences.frequentFlyerNumbers.length
                ? item.travelerPreferences.frequentFlyerNumbers
                  .filter((p) => p.selectedValue && p.selectedValue.value && p.value)
                  .reduce((acc, val) => {
                    acc[val.selectedValue.value] = val.value;
                    return acc;
                  }, {}) : undefined,
              emergencyContact: getEmergencyContactByPassenger(item, passengers),
              selectedVirtuosoCruiseBenefit: item.travelerPreferences
                .selectedVirtuosoCruiseBenefit > 0
                ? item.travelerPreferences.selectedVirtuosoCruiseBenefit : undefined,
              advancePassengerInformations: advancePassengerInformations.length
                ? advancePassengerInformations.map((p) => ({
                  ...p,
                  expirationDate: p.expirationDate ? dayjs(p.expirationDate).format('YYYY-MM-DD') : undefined,
                  visaIssueDate: p.visaIssueDate ? dayjs(p.visaIssueDate).format('YYYY-MM-DD') : undefined,
                })) : undefined,
            },
          };

          delete passenger.formValidations;
          if (!passenger.passengerOccupation) delete passenger.passengerOccupation;
          if (!passenger.insuranceCoverage) delete passenger.insuranceCoverage;
          if (!passenger.phoneType) delete passenger.phoneType;
          delete passenger.isPastPassenger;
          return passenger;
        }),
        creditCardPayments: buildCreditCardPayments({
          doIncludeCC,
          doUseSavedCardForBooking,
          creditCards: updatedCreditCards,
          tokenizedCardMap,
          isLoggedIn,
        }),
        agencyPlanningFees: doChargeAgencyPlanningFees ? agencyPlanningFees : undefined,
        agencyPlanningType: doChargeAgencyPlanningFees
          ? (agencyPlanningType || AgencyPlanningType.AgencyPlanningFeesPerTrip) : undefined,
        agencyPlanningFeesKeptByAgency: doChargeAgencyPlanningFees && doSplitServiceFees
          ? agencyPlanningFeesKeptByAgency : undefined,
        savedCreditCard: doUseSavedCardForBooking ? selectedCardForCheckout : undefined,
        cruisePayments: isBooking && cruisePayments && Object.values(cruisePayments).length
          ? _.filter(Object.values(cruisePayments),
            (c) => c !== null && c !== undefined) : undefined,
        itinerariesOnHold,
        tripChanges: tripChanges && Object.keys(tripChanges).length ? tripChanges : undefined,
        customerIdentifier: !validateCustomerIdentifier(customerIdentifier)
          ? customerIdentifier : undefined,
        requestorIATA: !validateRequestorIATA(requestorIATA)
          ? requestorIATA : undefined,
        promoCode: promoCode && promoCode.length && !validatePromoCode(promoCode)
          ? promoCode : undefined,
        travelAgencyAdvisorsEmail: trip?.advisorEmail
          || (travelAgencyAdvisorsEmail && travelAgencyAdvisorsEmail.length
            ? travelAgencyAdvisorsEmail
            : undefined),

        timeZoneOffset,
        doAcceptPriceQuoteChange,
        sessionKey,
        userRemarks: userRemarks && userRemarks.length ? userRemarks.map((remark) => ({
          ...remark,
          code: remark.code && remark.code.length ? remark.code : undefined,
          segment: undefined,
          id: undefined,
          segments: remark.segment && remark.segment.length ? _.map(remark.segment.split(','), (s) => +s) : undefined,
        })) : undefined,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    };
    primaryPassengerInfo = formatGuestInfoForVerifyingTripConfirmationAccess(
      passengers[0]?.firstName, passengers[0]?.lastName, passengers[0]?.contactEmail,
    );
    yield call([analytics, 'track'], 'bookTripWorker.bookTrip.sending', { tripId });
    const { data } = yield call(axios, config);
    if (data?.status === SearchStatus.SEARCHING) {
      yield put(pollBookingRequest({
        tripId,
        form: primaryPassengerInfo,
      }));
    } else if (tripConfirmationChanges && tripConfirmationChanges.doAddPaymentMethod
      && data && data.tripStatus !== BookingAction.BOOK) {
      yield put(bookTripFailure('Failed to confirm held cruise booking, please contact support!'));
    } else {
      yield call([analytics, 'track'], 'bookTripWorker.bookTrip.apiSuccess', { tripId });
      yield put(bookTripSuccess({
        data,
      }));

      // After a successful booking, check if a card was saved and if the saved card list
      // has already been loaded into the state, we need to refresh it to avoid showing stale data.
      const didRequestToSaveCard = Object.values(creditCards).some((card) => card.doSaveCard);
      if (didRequestToSaveCard) {
        const { getSavedCardsStatuses } = yield select((store) => store.customer);
        const customerId = yield select(getCustomerIdToSaveCard);
        const areCardsAlreadyLoaded = getSavedCardsStatuses && getSavedCardsStatuses[customerId];
        if (areCardsAlreadyLoaded) {
          yield put(getSavedCardsRequest({
            customerId,
            withAddressOnly: trip.isCardAddressRequired,
          }));
        }
      }

      yield put(changeTrip({
        field: 'userRemarks',
        value: [],
      }));
    }

    if (data && data.tripId) {
      yield put(getTripConfirmationSuccess({
        tripConfirmation: data,
        travelAgency,
      }));
      yield put(setCruiseItineraryPaymentAmount({
        tripConfirmation: data,
      }));
      yield put(changeValidation({
        emptyPassengerDetailValidation: {},
      }));
    }
  } catch (error) {
    console.error(error);
    const formattedError = formatBookingError(error);
    yield call([analytics, 'track'], 'bookTripWorker.bookTrip.fail', { tripId, error: formattedError?.error });

    if (formattedError.shouldPoll) {
      yield put(pollBookingRequest({ tripId, form: primaryPassengerInfo }));
      return;
    }

    if (formattedError.tripData) {
      yield put(getTripSuccess({
        tripId,
        ...formattedError.tripData,
      }));
    }

    yield put(bookTripFailure({
      error: formattedError.error,
      isTripAvailabilityPriceChanged: formattedError.isTripAvailabilityPriceChanged,
      tripAvailabilityPriceChangeResponse: formattedError.tripAvailabilityPriceChangeResponse,
    }));
  } finally {
    if (yield cancelled()) {
      yield put(changeTrip({ field: 'tripBookingStatus', value: SearchStatus.DONE }));
    }
  }
}

function* tripBookWatcher() {
  yield takeLatest(bookTripRequest.toString(), bookTripWorker);
}

export default tripBookWatcher;
